import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'semantic-ui-react';
import { Times, Bolt } from 'common/images/FaIcons';
import { useTranslation } from 'react-i18next';
import ConditionRow from './ConditionRow';
import EventCouplingAlternates from './EventCouplingAlternates';
import { getUserFriendlyError } from '../../utils/validationUtils';
import { isConditionCoupled } from '../../utils/formatConverters';
import { getAxisToBackendDirection } from '../../utils/directionLabels';
import {
  EventCard,
  EventCardHeader,
  EventCardTitle,
  Button,
  ConditionsSection,
  ConditionLogicRow,
  ConditionRowsContainer,
  TreeRoot,
  ActionSection,
  ActionSectionLabel,
  DropdownContainer,
  IconText,
  ValidationError
} from './EventDetection.styled';

// Map metric names to sensor type enum values
const METRIC_TO_SENSOR_TYPE = {
  Temperature: 0,
  Magnetometer: 1,
  Vibration: 2
};

const ACTIONS = [
  { key: 'immediate_upload', text: 'Immediate Upload', value: 'Immediate Upload', icon: Bolt }
];

const EventEditor = ({
  event,
  onUpdate,
  onRemove,
  showRemove,
  eventMetadata,
  vibrationDirectionPreference,
  temperatureOnly,
  magnetometerAvailable,
  validationErrors,
  alarmThresholds,
  fetchingAlarms,
  dashboardCouplingEligibility,
  fetchingDashboardEligibility,
  user
}) => {
  const { t } = useTranslation();
  const coupledToDashboard = !!(
    event.dashboard_coupling_config && event.dashboard_coupling_config.enabled
  );
  const handleAddCondition = useCallback(() => {
    if (coupledToDashboard) {
      return;
    }
    // Default to temperature sensor type when temperatureOnly is true
    const defaultMetric = temperatureOnly ? 'Temperature' : 'Vibration';
    const defaultMetricType = temperatureOnly ? 'Temperature' : 'Accel RMS';
    const defaultAxis = temperatureOnly ? [0] : [1, 2, 3];
    const defaultThresholds = temperatureOnly ? { value: 0 } : { x: 0, y: 0, z: 0 };

    const newCondition = {
      metric: defaultMetric,
      metricType: defaultMetricType,
      operator: '>',
      axis: defaultAxis,
      thresholds: defaultThresholds
    };
    onUpdate({ conditions: [...event.conditions, newCondition] });
  }, [event.conditions, temperatureOnly, onUpdate, coupledToDashboard]);

  // Helper function to map metricType to feature name
  const metricTypeToFeature = useCallback((metricType) => {
    if (metricType === 'Velocity RMS' || metricType === 'Velocity Peak' || metricType === 'Velocity P2P') {
      return 'velocity';
    }
    if (metricType === 'Accel RMS' || metricType === 'Accel Peak' || metricType === 'Accel P2P') {
      return 'acceleration';
    }
    return null;
  }, []);

  // Helper function to get sensor type value from metric name
  const getSensorTypeValue = useCallback(metric => (METRIC_TO_SENSOR_TYPE[metric] != null ? METRIC_TO_SENSOR_TYPE[metric] : null), []);

  // Helper function to update thresholds from alarm thresholds for a single condition
  const updateConditionThresholdsFromAlarms = useCallback((condition, couplingConfig, alarmThresholds) => {
    if (!couplingConfig || !alarmThresholds || !couplingConfig.coupled_sensor_types) {
      return condition;
    }

    const sensorTypeValue = getSensorTypeValue(condition.metric);
    const coupledSensorTypes = couplingConfig.coupled_sensor_types || [];

    // Only update if this condition's sensor type is coupled
    if (!coupledSensorTypes.includes(sensorTypeValue)) {
      return condition;
    }

    const updatedCondition = { ...condition };
    const thresholdLevel = couplingConfig.threshold_level || 'critical';

    // Update operator to ">" for coupled conditions
    updatedCondition.operator = '>';

    // Handle Temperature
    if (condition.metric === 'Temperature' && alarmThresholds.temperature) {
      const thresholdValue = alarmThresholds.temperature[thresholdLevel];
      if (thresholdValue !== undefined && thresholdValue !== null) {
        // Round to 2 decimal places to match stored values
        updatedCondition.thresholds = { value: parseFloat(thresholdValue.toFixed(2)) };
      }
    }

    // Handle Vibration
    if (condition.metric === 'Vibration' && alarmThresholds.vibration) {
      // Only update if metricType matches one of the selected features
      const conditionFeature = metricTypeToFeature(condition.metricType);
      const selectedFeatures = couplingConfig.vibration_features || ['velocity'];

      // Skip if this condition's feature is not in the selected features
      if (!conditionFeature || !selectedFeatures.includes(conditionFeature)) {
        return condition;
      }

      const speedBin = couplingConfig.speed_bin || 'bin_0';
      const axisToBackendDirection = getAxisToBackendDirection(vibrationDirectionPreference);
      // Normalize axis: for Vibration, empty/undefined axis means all axes [1,2,3]
      const axis = (condition.axis && condition.axis.length > 0) ? condition.axis : [1, 2, 3];

      // Ensure axis is set on the updated condition
      updatedCondition.axis = axis;

      const thresholdValues = {};
      axis.forEach((axisVal) => {
        if (axisVal !== 0) {
          const backendDirection = axisToBackendDirection[axisVal];
          if (backendDirection && alarmThresholds.vibration[backendDirection]) {
            const directionData = alarmThresholds.vibration[backendDirection][speedBin];
            if (directionData) {
              let value = null;

              if (conditionFeature === 'velocity') {
                value = directionData[thresholdLevel];
              } else if (conditionFeature === 'acceleration') {
                value = directionData[`acceleration_${thresholdLevel}`];
              }

              if (value !== undefined && value !== null) {
                let axisKey = 'x';
                if (axisVal === 2) axisKey = 'y';
                else if (axisVal === 3) axisKey = 'z';
                // Round to 2 decimal places to match stored values
                thresholdValues[axisKey] = parseFloat(value.toFixed(2));
              }
            }
          }
        }
      });

      // Only update if we have values for at least one axis
      if (Object.keys(thresholdValues).length > 0) {
        updatedCondition.thresholds = {
          x: thresholdValues.x !== undefined ? thresholdValues.x : (condition.thresholds && condition.thresholds.x) || 0,
          y: thresholdValues.y !== undefined ? thresholdValues.y : (condition.thresholds && condition.thresholds.y) || 0,
          z: thresholdValues.z !== undefined ? thresholdValues.z : (condition.thresholds && condition.thresholds.z) || 0
        };
      }
    }

    return updatedCondition;
  }, [getSensorTypeValue, metricTypeToFeature, vibrationDirectionPreference]);

  const handleUpdateCondition = useCallback((index, updates) => {
    const currentCondition = event.conditions[index];
    const updatedConditions = event.conditions.map((c, i) =>
      i === index ? { ...c, ...updates } : c
    );
    const updatedCondition = updatedConditions[index];

    // Check if metric or metricType changed
    const metricChanged = updates.metric && updates.metric !== currentCondition.metric;
    const metricTypeChanged = updates.metricType && updates.metricType !== currentCondition.metricType;

    // If metric or metricType changed and coupling is enabled, update thresholds from alarm thresholds
    if ((metricChanged || metricTypeChanged) && event.coupling_config && alarmThresholds) {
      const conditionWithUpdatedThresholds = updateConditionThresholdsFromAlarms(
        updatedCondition,
        event.coupling_config,
        alarmThresholds
      );

      // Update the condition with new thresholds
      updatedConditions[index] = conditionWithUpdatedThresholds;
    }

    onUpdate({ conditions: updatedConditions });
  }, [event.conditions, event.coupling_config, alarmThresholds, updateConditionThresholdsFromAlarms, onUpdate]);

  const handleRemoveCondition = useCallback((index) => {
    if (coupledToDashboard) {
      return;
    }
    const condition = event.conditions[index];
    const updatedConditions = event.conditions.filter((c, i) => i !== index);
    const updateData = { conditions: updatedConditions };

    if (
      event.coupling_config
      && isConditionCoupled(condition, event.coupling_config)
    ) {
      const sensorType = METRIC_TO_SENSOR_TYPE[condition.metric];
      const stillHasCoupledConditionOfType = updatedConditions.some(c =>
        METRIC_TO_SENSOR_TYPE[c.metric] === sensorType
        && isConditionCoupled(c, event.coupling_config)
      );

      if (!stillHasCoupledConditionOfType && sensorType != null) {
        const coupledTypes = event.coupling_config.coupled_sensor_types || [];
        updateData.coupling_config = {
          ...event.coupling_config,
          coupled_sensor_types: coupledTypes.filter(t => t !== sensorType)
        };
      }
    }

    onUpdate(updateData);
  }, [event.conditions, event.coupling_config, onUpdate, coupledToDashboard]);

  const handleLogicChange = useCallback((e, data) => {
    onUpdate({ logic: data.value === 'ALL' ? 'AND' : 'OR' });
  }, [onUpdate]);

  const handleActionChange = useCallback((e, data) => {
    onUpdate({ action: data.value });
  }, [onUpdate]);

  const handleCouplingChange = useCallback((couplingData) => {
    const updateData = {
      coupling_config: couplingData.coupling_config
    };

    if (couplingData.coupling_config) {
      updateData.dashboard_coupling_config = null;
    }

    if (couplingData.conditions) {
      updateData.conditions = couplingData.conditions;
    }

    onUpdate(updateData);
  }, [onUpdate]);

  const handleDashboardCouplingChange = useCallback((couplingData) => {
    onUpdate(couplingData);
  }, [onUpdate]);

  return (
    <EventCard hasErrors={validationErrors && !validationErrors.isValid}>
      <EventCardHeader>
        <EventCardTitle>
          <TreeRoot>●</TreeRoot>
          <span>{t('sensors.eventDetection.eventRule')}</span>
        </EventCardTitle>
        {showRemove && (
          <Button variant="ghost" size="sm" onClick={onRemove}>
            <Times />
          </Button>
        )}
      </EventCardHeader>

      {/* Display event-level validation errors */}
      {validationErrors && validationErrors.errors && validationErrors.errors.length > 0 && (
        <div style={{ padding: '0 16px', marginBottom: '8px' }}>
          {validationErrors.errors.map((error, idx) => (
            <ValidationError key={idx}>{getUserFriendlyError(error, t)}</ValidationError>
          ))}
        </div>
      )}

      <div style={{ padding: '0 16px', marginBottom: '16px' }}>
        <EventCouplingAlternates
          event={event}
          alarmThresholds={alarmThresholds}
          fetchingAlarms={fetchingAlarms}
          dashboardCouplingEligibility={dashboardCouplingEligibility}
          fetchingDashboardEligibility={fetchingDashboardEligibility}
          onAlarmCouplingChange={handleCouplingChange}
          onDashboardCouplingChange={handleDashboardCouplingChange}
          temperatureOnly={temperatureOnly}
          vibrationDirectionPreference={vibrationDirectionPreference}
          user={user}
        />
      </div>

      <ConditionsSection>
        <ConditionLogicRow>
          <span>{t('sensors.eventDetection.when')}</span>
          {event.conditions.length > 1 && (
            <>
              <DropdownContainer width="100px">
                <Dropdown
                  selection
                  value={event.logic === 'AND' ? 'ALL' : 'ANY'}
                  options={[
                    { key: 'all', text: t('sensors.eventDetection.logicAll'), value: 'ALL' },
                    { key: 'any', text: t('sensors.eventDetection.logicAny'), value: 'ANY' }
                  ]}
                  onChange={handleLogicChange}
                />
              </DropdownContainer>
              <span>{t('sensors.eventDetection.conditionsAll')}</span>
            </>
          )}
          {event.conditions.length === 1 && (
            <span>{t('sensors.eventDetection.conditionsSingle')}</span>
          )}
        </ConditionLogicRow>

        <ConditionRowsContainer>
          {event.conditions.map((condition, index) => {
            const isCoupled = (
              (event.coupling_config && isConditionCoupled(condition, event.coupling_config))
              || coupledToDashboard
            );
            return (
              <div key={index}>
                <ConditionRow
                  condition={condition}
                  onUpdate={updates => handleUpdateCondition(index, updates)}
                  onRemove={() => handleRemoveCondition(index)}
                  showRemove={event.conditions.length > 1 && !coupledToDashboard}
                  eventMetadata={eventMetadata}
                  vibrationDirectionPreference={vibrationDirectionPreference}
                  temperatureOnly={temperatureOnly}
                  magnetometerAvailable={magnetometerAvailable}
                  hasError={validationErrors && validationErrors.conditionErrors && validationErrors.conditionErrors[index]}
                  isCoupled={isCoupled}
                />
                {/* Display condition-specific validation errors */}
                {validationErrors && validationErrors.conditionErrors && validationErrors.conditionErrors[index] && (
                  <div style={{ marginTop: '4px', marginBottom: '8px' }}>
                    {validationErrors.conditionErrors[index].map((error, idx) => (
                      <ValidationError key={idx} small>{getUserFriendlyError(error, t)}</ValidationError>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </ConditionRowsContainer>

        <Button
          variant="outline"
          size="sm"
          onClick={handleAddCondition}
          disabled={coupledToDashboard}
        >
          {t('sensors.eventDetection.addCondition')}
        </Button>
      </ConditionsSection>

      <ActionSection>
        <ActionSectionLabel>{t('sensors.eventDetection.thenPerform')}</ActionSectionLabel>
        <DropdownContainer width="200px">
          <Dropdown
            selection
            disabled
            value={event.action}
            options={ACTIONS.map((action) => {
              const IconComponent = action.icon;
              return {
                key: action.key,
                text: (
                  <IconText>
                    <IconComponent style={{ fontSize: '0.9em' }} />
                    {t('sensors.eventDetection.actions.immediateUpload')}
                  </IconText>
                ),
                value: action.value
              };
            })}
            onChange={handleActionChange}
          />
        </DropdownContainer>
      </ActionSection>
    </EventCard>
  );
};

EventEditor.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string,
    conditions: PropTypes.array.isRequired,
    logic: PropTypes.oneOf(['AND', 'OR']).isRequired,
    action: PropTypes.string.isRequired,
    coupled_to_alarm_thresholds: PropTypes.bool,
    coupling_config: PropTypes.object,
    dashboard_coupling_config: PropTypes.object
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  showRemove: PropTypes.bool.isRequired,
  eventMetadata: PropTypes.object,
  vibrationDirectionPreference: PropTypes.number,
  temperatureOnly: PropTypes.bool,
  magnetometerAvailable: PropTypes.bool,
  validationErrors: PropTypes.shape({
    isValid: PropTypes.bool,
    errors: PropTypes.array,
    conditionErrors: PropTypes.object
  }),
  alarmThresholds: PropTypes.object,
  fetchingAlarms: PropTypes.bool,
  dashboardCouplingEligibility: PropTypes.object,
  fetchingDashboardEligibility: PropTypes.bool,
  user: PropTypes.object
};

EventEditor.defaultProps = {
  eventMetadata: null,
  vibrationDirectionPreference: 1,
  temperatureOnly: false,
  magnetometerAvailable: true,
  validationErrors: null,
  alarmThresholds: null,
  fetchingAlarms: false,
  dashboardCouplingEligibility: null,
  fetchingDashboardEligibility: false,
  user: null
};

export default EventEditor;
